import type { AudioChunk } from "../types";

interface RecorderOptions {
  chunkSeconds: number;
  onChunk: (chunk: AudioChunk) => void;
  onLevel: (rms: number) => void;
}

export interface RecorderDiagnostics {
  audioContextState: AudioContextState | "missing";
  fallbackBytes: number;
  fallbackChunks: number;
  fallbackDecodeError: string;
  fallbackMimeType: string;
  fallbackStopTimedOut: boolean;
  hasMediaRecorder: boolean;
  hasStream: boolean;
  levelPeakRms: number;
  levelPollCount: number;
  pcmChunkCount: number;
  pcmPeakRms: number;
  processorTicks: number;
  trackEnabled: boolean | null;
  trackLabel: string;
  trackMuted: boolean | null;
  trackReadyState: MediaStreamTrackState | "missing";
}

const TARGET_SAMPLE_RATE = 16_000;
const FALLBACK_RMS_THRESHOLD = 0.0001;
const MEDIA_RECORDER_MIME_TYPES = [
  "audio/mp4",
  "audio/aac",
  "audio/webm;codecs=opus",
  "audio/webm"
];
const FALLBACK_STOP_TIMEOUT_MS = 5000;
const LEVEL_POLL_MS = 100;

function createAudioContext(): AudioContext {
  const AudioContextClass =
    window.AudioContext ??
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextClass) {
    throw new Error("This browser does not support Web Audio.");
  }

  return new AudioContextClass();
}

function shouldSkipLocalAppleMobileFallback(): boolean {
  const isLocalHost =
    location.hostname === "localhost" ||
    location.hostname === "127.0.0.1" ||
    location.hostname === "::1";
  const userAgent = navigator.userAgent;
  const isAppleMobile =
    /iP(?:hone|ad|od)/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isWebKitSafari =
    /WebKit/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome|CriOS|Firefox|FxiOS|Edg|OPR/.test(userAgent);

  return isLocalHost && isAppleMobile && isWebKitSafari;
}

export class PcmChunkRecorder {
  private audioContext?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private processor?: ScriptProcessorNode;
  private analyser?: AnalyserNode;
  private sink?: MediaStreamAudioDestinationNode;
  private stream?: MediaStream;
  private mediaRecorder?: MediaRecorder;
  private mediaRecorderMimeType = "";
  private mediaRecorderChunks: Blob[] = [];
  private mediaRecorderStopped?: Promise<void>;
  private chunk?: Float32Array;
  private chunkSeconds = 0;
  private offset = 0;
  private energy = 0;
  private levelSamples?: Float32Array<ArrayBuffer>;
  private levelTimer?: number;
  private onChunk?: (chunk: AudioChunk) => void;
  private onLevel?: (rms: number) => void;
  private sequence = 0;
  private pcmChunkCount = 0;
  private pcmPeakRms = 0;
  private processorTicks = 0;
  private levelPollCount = 0;
  private levelPeakRms = 0;
  private fallbackBytes = 0;
  private fallbackChunks = 0;
  private fallbackDecodeError = "";
  private fallbackStopTimedOut = false;

  async start(options: RecorderOptions): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not expose microphone capture APIs.");
    }

    if (!window.isSecureContext && location.hostname !== "localhost") {
      throw new Error("Microphone capture requires HTTPS or localhost.");
    }

    this.onChunk = options.onChunk;
    this.onLevel = options.onLevel;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: { ideal: 1 },
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true }
      }
    });
    if (!shouldSkipLocalAppleMobileFallback()) {
      this.startEncodedFallbackRecorder();
    }

    this.audioContext = createAudioContext();
    await this.resumeAudioContext();
    this.chunkSeconds = options.chunkSeconds;
    this.resetChunk();

    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.levelSamples = new Float32Array(this.analyser.fftSize);
    this.sink = this.audioContext.createMediaStreamDestination();

    this.processor.onaudioprocess = (event) => {
      this.processorTicks += 1;
      this.processAudioBuffer(event.inputBuffer);
      for (let channel = 0; channel < event.outputBuffer.numberOfChannels; channel += 1) {
        event.outputBuffer.getChannelData(channel).fill(0);
      }
    };

    this.source.connect(this.processor);
    this.source.connect(this.analyser);
    this.processor.connect(this.sink);
    this.startLevelMonitor();
    await this.resumeAudioContext();
  }

  hasEncodedFallback(): boolean {
    return this.mediaRecorder !== undefined;
  }

  getDiagnostics(): RecorderDiagnostics {
    const track = this.stream?.getAudioTracks()[0];

    return {
      audioContextState: this.audioContext?.state ?? "missing",
      fallbackBytes: this.fallbackBytes,
      fallbackChunks: this.fallbackChunks,
      fallbackDecodeError: this.fallbackDecodeError,
      fallbackMimeType: this.mediaRecorderMimeType,
      fallbackStopTimedOut: this.fallbackStopTimedOut,
      hasMediaRecorder: this.mediaRecorder !== undefined,
      hasStream: this.stream !== undefined,
      levelPeakRms: this.levelPeakRms,
      levelPollCount: this.levelPollCount,
      pcmChunkCount: this.pcmChunkCount,
      pcmPeakRms: this.pcmPeakRms,
      processorTicks: this.processorTicks,
      trackEnabled: track?.enabled ?? null,
      trackLabel: track?.label ?? "",
      trackMuted: track?.muted ?? null,
      trackReadyState: track?.readyState ?? "missing"
    };
  }

  async stop(): Promise<RecorderDiagnostics> {
    this.stopLevelMonitor();
    this.flushChunk();
    await new Promise((resolve) => window.setTimeout(resolve, 80));
    const liveDiagnostics = this.getDiagnostics();
    const fallbackBlob = await this.stopEncodedFallbackRecorder();

    if (fallbackBlob && (this.pcmChunkCount === 0 || this.pcmPeakRms < FALLBACK_RMS_THRESHOLD)) {
      await this.emitFallbackChunks(fallbackBlob);
    }

    const diagnostics: RecorderDiagnostics = {
      ...liveDiagnostics,
      fallbackBytes: this.fallbackBytes,
      fallbackChunks: this.fallbackChunks,
      fallbackDecodeError: this.fallbackDecodeError,
      fallbackMimeType: this.mediaRecorderMimeType,
      fallbackStopTimedOut: this.fallbackStopTimedOut,
      levelPeakRms: this.levelPeakRms,
      levelPollCount: this.levelPollCount,
      pcmChunkCount: this.pcmChunkCount,
      pcmPeakRms: this.pcmPeakRms,
      processorTicks: this.processorTicks
    };
    this.source?.disconnect();
    this.processor?.disconnect();
    if (this.processor) {
      this.processor.onaudioprocess = null;
    }
    this.analyser?.disconnect();
    this.sink?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());

    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }

    this.audioContext = undefined;
    this.source = undefined;
    this.processor = undefined;
    this.analyser = undefined;
    this.sink = undefined;
    this.stream = undefined;
    this.mediaRecorder = undefined;
    this.mediaRecorderChunks = [];
    this.mediaRecorderStopped = undefined;
    this.mediaRecorderMimeType = "";
    this.chunk = undefined;
    this.chunkSeconds = 0;
    this.offset = 0;
    this.energy = 0;
    this.levelSamples = undefined;

    return diagnostics;
  }

  private async resumeAudioContext(): Promise<void> {
    if (!this.audioContext || this.audioContext.state !== "suspended") {
      return;
    }

    try {
      await this.audioContext.resume();
    } catch {
      // Safari can reject resume even after a tap. The encoded fallback still gives us a chance to transcribe.
    }
  }

  private resetChunk(): void {
    const sampleRate = this.audioContext?.sampleRate ?? TARGET_SAMPLE_RATE;
    const targetFrames = Math.max(1, Math.round(sampleRate * this.chunkSeconds));
    this.chunk = new Float32Array(targetFrames);
    this.offset = 0;
    this.energy = 0;
  }

  private processAudioBuffer(inputBuffer: AudioBuffer): void {
    const channelCount = Math.max(1, inputBuffer.numberOfChannels);
    const channels = Array.from({ length: channelCount }, (_, index) => inputBuffer.getChannelData(index));

    for (let frame = 0; frame < inputBuffer.length; frame += 1) {
      let sample = 0;

      for (const channel of channels) {
        sample += channel[frame] ?? 0;
      }

      this.appendSample(sample / channelCount);
    }
  }

  private appendSample(sample: number): void {
    if (!this.chunk) {
      return;
    }

    this.chunk[this.offset] = sample;
    this.energy += sample * sample;
    this.offset += 1;

    if (this.offset >= this.chunk.length) {
      this.flushChunk();
    }
  }

  private flushChunk(): void {
    if (!this.chunk || this.offset === 0) {
      return;
    }

    const samples = this.chunk.slice(0, this.offset);
    const rms = Math.sqrt(this.energy / Math.max(1, this.offset));
    const sequence = ++this.sequence;
    this.pcmChunkCount += 1;
    this.pcmPeakRms = Math.max(this.pcmPeakRms, rms);
    this.onChunk?.({
      id: this.createChunkId(sequence),
      sequence,
      sampleRate: this.audioContext?.sampleRate ?? TARGET_SAMPLE_RATE,
      samples,
      rms,
      durationSeconds: samples.length / (this.audioContext?.sampleRate ?? TARGET_SAMPLE_RATE)
    });
    this.resetChunk();
  }

  private startLevelMonitor(): void {
    this.stopLevelMonitor();
    this.emitAnalyserLevel();
    this.levelTimer = window.setInterval(() => this.emitAnalyserLevel(), LEVEL_POLL_MS);
  }

  private stopLevelMonitor(): void {
    if (this.levelTimer === undefined) {
      return;
    }

    window.clearInterval(this.levelTimer);
    this.levelTimer = undefined;
  }

  private emitAnalyserLevel(): void {
    if (!this.analyser || !this.levelSamples) {
      return;
    }

    this.analyser.getFloatTimeDomainData(this.levelSamples);
    let energy = 0;

    for (let index = 0; index < this.levelSamples.length; index += 1) {
      const sample = this.levelSamples[index] ?? 0;
      energy += sample * sample;
    }

    const rms = Math.sqrt(energy / Math.max(1, this.levelSamples.length));
    this.levelPollCount += 1;
    this.levelPeakRms = Math.max(this.levelPeakRms, rms);
    this.onLevel?.(rms);
  }

  private startEncodedFallbackRecorder(): void {
    const MediaRecorderClass = window.MediaRecorder;
    if (!MediaRecorderClass || !this.stream) {
      return;
    }

    const mimeType =
      MEDIA_RECORDER_MIME_TYPES.find((candidate) => MediaRecorderClass.isTypeSupported(candidate)) ??
      "";

    try {
      this.mediaRecorderMimeType = mimeType;
      this.mediaRecorderChunks = [];
      this.mediaRecorder = mimeType
        ? new MediaRecorderClass(this.stream, { mimeType })
        : new MediaRecorderClass(this.stream);
      this.mediaRecorderStopped = new Promise((resolve) => {
        this.mediaRecorder?.addEventListener(
          "dataavailable",
          (event) => {
            if (event.data.size > 0) {
              this.mediaRecorderChunks.push(event.data);
              this.fallbackBytes += event.data.size;
            }
          },
          false
        );
        this.mediaRecorder?.addEventListener("stop", () => resolve(), { once: true });
      });
      this.mediaRecorder.start();
    } catch {
      this.mediaRecorder = undefined;
      this.mediaRecorderChunks = [];
      this.mediaRecorderStopped = undefined;
      this.mediaRecorderMimeType = "";
    }
  }

  private async stopEncodedFallbackRecorder(): Promise<Blob | null> {
    const recorder = this.mediaRecorder;
    if (!recorder) {
      return null;
    }

    const stopped = this.mediaRecorderStopped;
    if (recorder.state !== "inactive") {
      try {
        recorder.requestData();
      } catch {
        // Some browsers do not allow requestData this close to stop.
      }

      try {
        recorder.stop();
      } catch {
        return null;
      }
    }

    if (stopped) {
      const stoppedCleanly = await Promise.race([
        stopped.then(() => true),
        new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), FALLBACK_STOP_TIMEOUT_MS))
      ]);
      this.fallbackStopTimedOut = !stoppedCleanly;
    }

    if (this.mediaRecorderChunks.length === 0) {
      return null;
    }

    return new Blob(this.mediaRecorderChunks, {
      type: this.mediaRecorderMimeType || this.mediaRecorderChunks[0]?.type || "audio/webm"
    });
  }

  private async emitFallbackChunks(blob: Blob): Promise<void> {
    const audioContext = this.audioContext ?? createAudioContext();
    const shouldCloseContext = audioContext !== this.audioContext;
    this.audioContext = audioContext;

    try {
      const decoded = await audioContext.decodeAudioData(await blob.arrayBuffer());
      const channelCount = Math.max(1, decoded.numberOfChannels);
      const channels = Array.from({ length: channelCount }, (_, index) => decoded.getChannelData(index));
      const framesPerChunk = Math.max(1, Math.round(decoded.sampleRate * 0.5));

      for (let offset = 0; offset < decoded.length; offset += framesPerChunk) {
        const frameCount = Math.min(framesPerChunk, decoded.length - offset);
        const samples = new Float32Array(frameCount);
        let energy = 0;

        for (let frame = 0; frame < frameCount; frame += 1) {
          let sample = 0;
          for (const channel of channels) {
            sample += channel[offset + frame] ?? 0;
          }
          sample /= channelCount;
          samples[frame] = sample;
          energy += sample * sample;
        }

        const sequence = ++this.sequence;
        this.onChunk?.({
          id: this.createChunkId(sequence),
          sequence,
          sampleRate: decoded.sampleRate,
          samples,
          rms: Math.sqrt(energy / Math.max(1, frameCount)),
          durationSeconds: frameCount / decoded.sampleRate
        });
        this.fallbackChunks += 1;
      }
    } catch (error) {
      this.fallbackDecodeError = error instanceof Error ? error.message : String(error);
    } finally {
      if (shouldCloseContext && audioContext.state !== "closed") {
        await audioContext.close();
        this.audioContext = undefined;
      }
    }
  }

  private createChunkId(sequence: number): string {
    return (
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${sequence}-${Math.random().toString(16).slice(2)}`
    );
  }
}

export function resampleTo16Khz(samples: Float32Array, sampleRate: number): Float32Array {
  if (sampleRate === TARGET_SAMPLE_RATE) {
    return samples.slice();
  }

  const ratio = sampleRate / TARGET_SAMPLE_RATE;
  const outputLength = Math.max(1, Math.round(samples.length / ratio));
  const output = new Float32Array(outputLength);

  for (let index = 0; index < outputLength; index += 1) {
    const sourceIndex = index * ratio;
    const leftIndex = Math.floor(sourceIndex);
    const rightIndex = Math.min(samples.length - 1, leftIndex + 1);
    const weight = sourceIndex - leftIndex;
    const left = samples[leftIndex] ?? 0;
    const right = samples[rightIndex] ?? left;
    output[index] = left + (right - left) * weight;
  }

  return output;
}

export function rmsToPercent(rms: number): number {
  const scaled = Math.min(1, Math.max(0, Math.sqrt(Math.max(0, rms)) * 3.2));
  return Math.round(scaled * 100);
}
